/**
 * the ExternalClickWrapper listens for a mouse click or ESC keypress outside of the children
 * container and fires onExternalClick
 * modfied from https://github.com/react-bootstrap/react-overlays
 */
// tslint:disable-next-line:no-var-requires
const contains = require('dom-helpers/query/contains');
// tslint:disable-next-line:no-var-requires
const DomAddEventListener = require('dom-helpers/events/on');
// tslint:disable-next-line:no-var-requires
const DomRemoveEventListener = require('dom-helpers/events/off');
// tslint:disable-next-line:no-var-requires
const DomOwnerDocument = require('dom-helpers/ownerDocument');

import React, { Component, PropsWithChildren } from 'react';
import ReactDOM from 'react-dom';
import { ExternalClickEvents, IExternalClickWrapperProps, K, M } from './types';

const escapeKeyCode = 27;

function isLeftClickEvent(event: M) {
  return event.button === 0;
}

function isModifiedEvent(event: M) {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

function addEventListener(node: Element, event: ExternalClickEvents, handler: (e: K | M) => void, capture?: boolean) {
  DomAddEventListener(node, event, handler, capture);

  return {
    remove() {
      DomRemoveEventListener(node, event, handler, capture);
    },
  };
}

function ownerDocument(componentOrElement: React.ReactInstance) {
  return DomOwnerDocument(ReactDOM.findDOMNode(componentOrElement));
}

/**
 * The `<ExternalClickWrapper>` component is used to apply modal style behavior to the content
 * of the wrapper.  ExternalClickWrapper wraps your component and listens for an external
 * interaction (i.e. mouse click or **esc** key).  When it detects an external event it triggers
 * the **onExternal** callback.
 */
export class ExternalClickWrapper extends Component<PropsWithChildren<IExternalClickWrapperProps>, {}> {
  public static defaultProps = {
    event: 'click',
  };

  public documentMouseCaptureListener: { remove: () => void }; // an event listener
  public documentMouseListener: { remove: () => void }; // an event listener
  public documentKeyupListener: { remove: () => void }; // an event listener
  public preventMouseRootClose: boolean;

  // tslint:disable-next-line
  constructor(props: IExternalClickWrapperProps, context: any) {
    super(props, context);
    this.preventMouseRootClose = false;
  }

  public componentDidMount() {
    if (!this.props.disabled) {
      this.addEventListeners();
    }
  }

  public componentDidUpdate(prevProps: IExternalClickWrapperProps) {
    if (!this.props.disabled && prevProps.disabled) {
      this.addEventListeners();
    } else if (this.props.disabled && !prevProps.disabled) {
      this.removeEventListeners();
    }
  }

  public componentWillUnmount() {
    if (!this.props.disabled) {
      this.removeEventListeners();
    }
  }

  public addEventListeners = () => {
    const { event = 'click' } = this.props;
    const doc = ownerDocument(this);

    // Use capture for this listener so it fires before React's listener, to
    // avoid false positives in the contains() check below if the target DOM
    // element is removed in the React mouse callback.
    this.documentMouseCaptureListener = addEventListener(doc, event, this.handleMouseCapture, true);

    /**
     * Also putting this at capture phase because
     * 1. So that it works if the event stopped propagation between the container and dom
     * 2. In many cases clicking on the trigger to open it is also "outside",
     *    so having the close happen at capture means the normal handler can keep it open
     */
    this.documentMouseListener = addEventListener(doc, event, this.handleMouse, true);

    this.documentKeyupListener = addEventListener(doc, 'keyup', this.handleKeyUp);
  };

  public removeEventListeners = () => {
    if (this.documentMouseCaptureListener) {
      this.documentMouseCaptureListener.remove();
    }

    if (this.documentMouseListener) {
      this.documentMouseListener.remove();
    }

    if (this.documentKeyupListener) {
      this.documentKeyupListener.remove();
    }
  };

  public handleMouseCapture = (e: M) => {
    /**
     * In cases where we have an ExternalClickWrapper surrounding a "portaled" element the ExternalClickWrapper
     * will trigger an onExternal event when it hears a click on the portaled element which often results in closing the portal.
     * In order to ignore clicks on the portaled element add the class 'ignore-external-click' on the portal wrapper.
     * Note:  Currently this only works for portaled elements because the logic below is checking children of
     * body that have the class ignore-external-click which it assumes to be portaled elements.
     */
    const ignoreClick = Array.from(document.body.children)
      .filter((child) => child.classList.contains('ignore-external-click'))
      .find((portal) => {
        return contains(portal, e.target);
      });

    if (ignoreClick) {
      this.preventMouseRootClose = true;
    } else {
      this.preventMouseRootClose =
        isModifiedEvent(e) || !isLeftClickEvent(e) || contains(ReactDOM.findDOMNode(this), e.target);
    }
  };

  public handleMouse = (e: M) => {
    if (!this.preventMouseRootClose && this.props.onExternal) {
      this.props.onExternal(e);
    }
  };

  public handleKeyUp = (e: K) => {
    if (e.keyCode === escapeKeyCode && this.props.onExternal) {
      this.props.onExternal(e);
    }
  };

  public render() {
    return this.props.children;
  }
}

export default ExternalClickWrapper;
